import React, { useState, useEffect, useCallback } from 'react';
import { MenuItem, Tooltip, Typography, IconButton, Drawer, Grid, Divider } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { addAlert } from 'store/modules/snackBar';
import {
  OperacaoCommonStatus,
  OperacaoOfertaPrimariaResponse,
  GrupoAcesso,
  FuncaoParticipante,
  OfertaPrimariaStatus,
  OperacaoStatus,
  CompraVendaStatus,
  Perfil,
  OfertasPrimariasFilter,
  AgendamentoHandleAction,
  AgendamentoMovimentacao,
  ApproveOfertaPrimariaRequest,
  TipoLiquidacao,
  InstrumentoFinanceiro,
  Comprovante,
  ComprovanteInvestidorResponse,
} from 'common/genericTypes';
import { operacaoStatusDescriptions, operacaoTipoDescriptions } from 'common/genericDescriptions';
import { Button, LgSpace, MdSpace, MoreOptionsMenu, SmSpace } from 'm2m.components';
import { GenericDrawer } from 'components/genericDrawer';
import { useGenericStyles } from 'common/genericStyles';
import { Cancel, CheckCircle, Close, WatchLater, Error, ReportProblemOutlined, Description, CalendarToday, Edit, AttachFile, GetApp } from '@material-ui/icons';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { v4 as uuid } from 'uuid';
import { DatePickerBusinessDay } from 'components/DatePickerBussinessDay';
import { RootState } from 'store';
import LegendaStatus from 'pages/operacoes/ofertaPrimaria/legendaStatus';
import { formatDate } from 'utils/date';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import ToolbarExportacaoMovimentacoes from 'utils/dataGrid/toolbarExportacaoMovimentacoes';
import { colunasRelatorioMovimentacao } from 'pages/operacoes/gerenciar/colunasRelatorioMovimentacao';
import { colunaDocumentoOfertaPrimaria } from 'pages/operacoes/gerenciar/colunaDocumentoMovimentacao';
import {
  DataGridPro,
  GridCellParams,
  GridValueGetterParams,
  GridSortModel,
  GridFilterModel,
  getGridStringOperators,
  getGridDateOperators,
  getGridSingleSelectOperators,
  GridLinkOperator,
} from '@mui/x-data-grid-pro';

enum DrawerAction {
  APROVAR,
  REJEITAR,
  AGENDAMENTO,
  GESTAO_COMPROVANTES,
}

interface Props {
  filtros?: string;
}

const OfertasPrimarias: React.FC<Props> = (props) => {
  const { filtros } = props;
  const classes = useGenericStyles();
  const history = useHistory();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [pageCount, setPageCount] = useState<number>(0);
  const [operacoes, setOperacoes] = useState<Array<OperacaoOfertaPrimariaResponse>>([]);
  const [operacao, setOperacao] = useState<OperacaoOfertaPrimariaResponse>();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openAgendamentoDrawer, setOpenAgendamentoDrawer] = useState<boolean>(false);
  const [dataAgendamento, setDataAgendamento] = useState<moment.Moment | null>(null);
  const [agendamentoAtual, setAgendamentoAtual] = useState<AgendamentoMovimentacao | null>(null);
  const [drawerAction, setDrawerAction] = useState<DrawerAction>(DrawerAction.APROVAR);
  const [showStatusToast, setShowStatusToast] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [openGestaoComprovantesDrawer, setOpenGestaoComprovantesDrawer] = useState<boolean>(false);
  const [gestaoComprovantesAttachments, setGestaoComprovantesAttachments] = useState<Array<Attachment>>([]);
  const [isSavingComprovantes, setIsSavingComprovantes] = useState<boolean>(false);
  const [comprovantesExistentes, setComprovantesExistentes] = useState<Array<ComprovanteInvestidorResponse & { investidorId: string }>>([]);
  const [loadingComprovantes, setLoadingComprovantes] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [ofertasPrimariasFilter, setOfertasPrimariasFilter] = useState<Array<OfertasPrimariasFilter>>([]);
  const { postLiquidarOfertaPrimaria } = useSelector((state: RootState) => state.operacaoApi);
  

  const isUsuarioOperacoes = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes
  );
  const isParticipanteCustodiante = useSelector(
    (state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.Custodiante
  );
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);
  const isParticipanteAdministradorFundo = useSelector(
    (state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.AdministradorFiduciario
  );

  const filterOperatorsSelect = getGridSingleSelectOperators().filter(({ value }) => ['is', 'not'].includes(value));

  const {
    getOperacoesOfertaPrimariaPaginated,
    postOfertasPrimariasRelatorioCompleto,
    postAprovarOfertaPrimaria,
    postReprovarOfertaPrimaria,
    putAgendamentoOfertaPrimaria,
    getAgendamentosOfertaPrimaria,
    postComprovanteInvestidorById,
    getComprovantesInvestidor,
    getOfertaPrimariaComprovanteDownloadUrl,
    getComprovanteInvestidorDownloadUrl,
  } = useSelector((state: RootState) => state.operacaoApi);

  const handleOpenDrawer = (action: DrawerAction, op: OperacaoOfertaPrimariaResponse) => {
    setShowStatusToast(false);
    setDrawerAction(action);
    setOperacao(op);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setOperacao(undefined);
  };

  const handleOpenAgendamentoDrawer = async (operacao: OperacaoOfertaPrimariaResponse) => {
    setOperacao(operacao);
    setShowLoader(true);
    try {
      const agendamentos = await getAgendamentosOfertaPrimaria(dispatch, operacao.id);

      if (agendamentos && agendamentos.length > 0) {
        setAgendamentoAtual(agendamentos[0]);
        setDataAgendamento(moment(agendamentos[0].dataAgendamento));
      }
    } catch (error) {
      setAgendamentoAtual(null);
    } finally {
      setShowLoader(false);
      setOpenAgendamentoDrawer(true);
    }
  };

  const handleCloseAgendamentoDrawer = () => {
    setOpenAgendamentoDrawer(false);
    setOperacao(undefined);
    setDataAgendamento(null);
  };

  const handleOpenGestaoComprovantesDrawer = (op: OperacaoOfertaPrimariaResponse) => {
    setOperacao(op);
    setGestaoComprovantesAttachments([]);
    setComprovantesExistentes([]);
    setOpenGestaoComprovantesDrawer(true);

    const investidores = op.ofertaPrimaria?.investidores ?? [];
    if (investidores.length > 0) {
      setLoadingComprovantes(true);
      Promise.all(
        investidores.map((inv) =>
          getComprovantesInvestidor(dispatch, op.id, inv.investidorId, false)
            .then((result) => (result ?? []).map((comp) => ({ ...comp, investidorId: inv.investidorId })))
            .catch(() => [] as Array<ComprovanteInvestidorResponse & { investidorId: string }>)
        )
      )
        .then((results) => {
          setComprovantesExistentes(results.flat());
        })
        .finally(() => setLoadingComprovantes(false));
    }
  };

  const handleCloseGestaoComprovantesDrawer = () => {
    setOpenGestaoComprovantesDrawer(false);
    setOperacao(undefined);
    setGestaoComprovantesAttachments([]);
    setComprovantesExistentes([]);
  };

  const handleSalvarComprovantes = async () => {
    if (!operacao || gestaoComprovantesAttachments.length === 0) return;
    setIsSavingComprovantes(true);
    try {
      const investidores = operacao.ofertaPrimaria?.investidores ?? [];
      const files = gestaoComprovantesAttachments.filter((att) => !!att.file).map((att) => att.file!);

      await Promise.all(
        investidores.map((inv) =>
          postComprovanteInvestidorById(dispatch, operacao.id, inv.investidorId, files, true)
        )
      );

      await getOperacoes();
      dispatch(addAlert({ severity: 'success', message: 'Comprovante(s) anexado(s) com sucesso.' }));
      handleCloseGestaoComprovantesDrawer();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingComprovantes(false);
    }
  };

  const handleDownloadComprovante = async (comprovanteId: string, investidorId: string, fileName: string) => {
    if (!operacao) return;
    try {
      const result = await getComprovanteInvestidorDownloadUrl(dispatch, operacao.id, investidorId, comprovanteId, true);
      if (result) {
        const link = document.createElement('a');
        link.href = result;
        link.target = '_blank';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditarAgendamento = async () => {
    if (operacao && dataAgendamento) {
      setShowLoader(true);
      try {
        await putAgendamentoOfertaPrimaria(
          dispatch,
          {
            operacaoId: operacao.id,
            data: dataAgendamento.toISOString(),
            action: AgendamentoHandleAction.ALTERAR
          }
        );
        setShowStatusToast(true);
        handleCloseAgendamentoDrawer();
        getOperacoes();
      } catch (error) {
        setShowStatusToast(true);
      } finally {
        try {
          const agendamentos = await getAgendamentosOfertaPrimaria(dispatch, operacao.id);

          if (agendamentos && agendamentos.length > 0) {
            setAgendamentoAtual(agendamentos[0]);
            setDataAgendamento(moment(agendamentos[0].dataAgendamento));
          }
        } catch (error) {
          setShowStatusToast(true);
        }
        setShowLoader(false);
      }
    }
  };

  const handleCancelarAgendamento = async () => {
    if (operacao) {
      setShowLoader(true);
      try {
        await putAgendamentoOfertaPrimaria(
          dispatch,
          {
            operacaoId: operacao.id,
            data: null,
            action: AgendamentoHandleAction.CANCELAR
          }
        );

        setShowStatusToast(true);
        handleCloseAgendamentoDrawer();
        getOperacoes();
      } catch (error) {
        setShowStatusToast(true);
      } finally {
        setShowLoader(false);
      }
    }
  };

  const getOperacoes = useCallback(async () => {
    try {
      setShowLoader(true);
      const result = await getOperacoesOfertaPrimariaPaginated(
        dispatch,
        { page: page + 1, pageSize, linkOperator: linkOperator, ofertasPrimariasFilter: ofertasPrimariasFilter },
        true
      );
      setOperacoes(result.result);
      setPageCount(result.totalCount);
      setShowLoader(false);
    } catch {
      setShowLoader(false);
    }
  }, [dispatch, getOperacoesOfertaPrimariaPaginated, ofertasPrimariasFilter, page, pageSize, linkOperator]);

  const carregarTodasAsMovimentacoes = useCallback(
    () =>
      postOfertasPrimariasRelatorioCompleto(
        dispatch,
        {
          linkOperator,
          ofertasPrimariasFilter,
        },
        true
      ),
    [dispatch, postOfertasPrimariasRelatorioCompleto, ofertasPrimariasFilter, linkOperator]
  );

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!filterModel.items) {
        setOfertasPrimariasFilter([]);
        return;
      }
      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      var ofertasPrimariasFilter = new Array<OfertasPrimariasFilter>();

      filterModel.items.forEach((filter) => {
        let operatorValue =
          filter.operatorValue == 'startsWith' ? 'STARTS_WITH' : filter.operatorValue == 'endsWith'
            ? 'ENDS_WITH' : filter.operatorValue?.toUpperCase();

        let formattedValue = filter.value?.toString();
        if (formattedValue) {
          if (filter.columnField == 'tipo') {
            formattedValue = formattedValue.replace('_', ' ');
          }
          if (filter.columnField == 'CRIADO_EM' || filter.columnField == 'DATA_LIQUIDACAO') {
            formattedValue = formattedValue.split('-');
            formattedValue = `${formattedValue[2]}/${formattedValue[1]}/${formattedValue[0]}`;
          }

          let params = {
            columnType: filter.columnField.toUpperCase(),
            operationFilterType: operatorValue,
            value: formattedValue,
          } as OfertasPrimariasFilter;

          ofertasPrimariasFilter.push(params);
        }
        else {
          if (operatorValue == 'ISNOTEMPTY' || operatorValue == 'ISEMPTY') {
            ofertasPrimariasFilter.push({
              columnType: filter.columnField.toUpperCase(),
              operationFilterType: operatorValue,
              value: '',
            } as OfertasPrimariasFilter);
          }
        }
      });

      setOfertasPrimariasFilter(ofertasPrimariasFilter);
    },
    [getOperacoes, page, pageSize, linkOperator, ofertasPrimariasFilter]
  );

  const onStatusClick = useCallback(
    (statusToFilter: string) => {
      const statusFilter: GridFilterModel = {
        items: [
          {
            columnField: 'STATUS',
            operatorValue: 'CONTAINS',
            value: statusToFilter,
          },
        ],
        linkOperator: GridLinkOperator.And,
      };

      onFilterChange(statusFilter);
    },
    [onFilterChange]
  );

  const onSortChange = useCallback(
    (filterModel: GridSortModel) => {
      if (filterModel == null) return;

      var filter = filterModel[0];
      let params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as OfertasPrimariasFilter;

      var newEFFilter = ofertasPrimariasFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<OfertasPrimariasFilter>;
      newEFFilter.push(params);
      setOfertasPrimariasFilter([...newEFFilter]);
    },
    [getOperacoes, page, pageSize, ofertasPrimariasFilter]
  );

  const onDetalhesFunction = (operacao: OperacaoOfertaPrimariaResponse) => {
    return history.push(`/operacoes/gerenciar/ofertas-primarias/${operacao.id}`);
  };

  const statusToastByAction = useCallback(
    (action: DrawerAction) => {
      let divClassName: string = '';
      let resultDescription: string = '';
      let icon: React.ReactNode | null = null;
      switch (action) {
        case DrawerAction.REJEITAR:
          divClassName = classes.errorToast;
          resultDescription = 'rejeitada';
          icon = <Cancel style={{ marginRight: 10 }} />;
          break;
        case DrawerAction.APROVAR:
          divClassName = classes.successToast;
          resultDescription = 'aprovada';
          icon = <CheckCircle style={{ marginRight: 10 }} />;
          break;
      }
      return (
        <>
          <SmSpace />
          <div className={divClassName}>
            {icon}
            <div style={{ display: 'flex', flex: 1 }}>
              <Typography variant="body2">{'Movimentação por'}</Typography>&nbsp;
              <Typography>{`Oferta Primária ${resultDescription}`}</Typography>&nbsp;
              <Typography variant="body2">{'com sucesso'}</Typography>
            </div>
            <IconButton onClick={() => setShowStatusToast(false)}>
              <Close />
            </IconButton>
          </div>
        </>
      );
    },
    [classes]
  );

  const handleAprovar = async () => {
    try {
      if (operacao) {
        if (operacao.instrumentoFinanceiro.tipoInstrumentoFinanceiro === InstrumentoFinanceiro.CotasDeFundo) {
          await postLiquidarOfertaPrimaria(
            dispatch, 
            operacao.id, 
            {
              tipoLiquidacao: TipoLiquidacao.Direta,
              custodianteId: operacao.custodiante.id,
            }, 
          true);  
        } else {
          await postAprovarOfertaPrimaria(dispatch, operacao.id);
        }

        await getOperacoes();
        
        handleCloseDrawer();
        setShowStatusToast(true);
      }
    } catch (error) { }
  };

  const handleReprovar = async () => {
    try {
      if (operacao) {
        await postReprovarOfertaPrimaria(dispatch, operacao.id);
        await getOperacoes();
        handleCloseDrawer();
        setShowStatusToast(true);
      }
    } catch (error) { }
  };

  const onAprovarFunction = (operacao: OperacaoOfertaPrimariaResponse) => {

    if (
      isUsuarioOperacoes && 
      operacao.instrumentoFinanceiro.tipoInstrumentoFinanceiro !== InstrumentoFinanceiro.CotasDeFundo && 
      operacao.status === OperacaoCommonStatus.EmAnalise
    ) {
      return () => handleOpenDrawer(DrawerAction.APROVAR, operacao);
    }

    if (
      isParticipanteAdministradorFundo && 
      operacao.instrumentoFinanceiro.tipoInstrumentoFinanceiro === InstrumentoFinanceiro.CotasDeFundo && 
      operacao.status === OperacaoCommonStatus.EmAnalise
    ) {
      return () => handleOpenDrawer(DrawerAction.APROVAR, operacao);
    }

    return undefined;
  };

  const onConfirmarFunction = (operacao: OperacaoOfertaPrimariaResponse) => {

    if (isParticipanteCustodiante && operacao.status === OfertaPrimariaStatus.AguardandoConfirmacao) {
      return () => history.push(`/operacoes/gerenciar/ofertas-primarias/${operacao.id}`, { initialTab: 1 });
    }
    return undefined;
  };

  const onReprovarFunction = (operacao: OperacaoOfertaPrimariaResponse) => {
    if (
      isUsuarioOperacoes &&
      operacao.instrumentoFinanceiro.tipoInstrumentoFinanceiro !== InstrumentoFinanceiro.CotasDeFundo && 
      operacao.status !== OperacaoCommonStatus.Concluida &&
      operacao.status !== OperacaoCommonStatus.Reprovada
    ) {
      return () => handleOpenDrawer(DrawerAction.REJEITAR, operacao);
    }

    if (
      isParticipanteAdministradorFundo && 
      operacao.instrumentoFinanceiro.tipoInstrumentoFinanceiro === InstrumentoFinanceiro.CotasDeFundo && 
      operacao.status === OperacaoCommonStatus.EmAnalise
    ) {
      return () => handleOpenDrawer(DrawerAction.REJEITAR, operacao);
    }

    if (isParticipanteCustodiante && operacao.status === OfertaPrimariaStatus.AguardandoConfirmacao) {
      return () => handleOpenDrawer(DrawerAction.REJEITAR, operacao);
    }
    return undefined;
  };

  useEffect(() => {
    getOperacoes();
  }, [getOperacoes, page, ofertasPrimariasFilter]);

  const getStatusIcon = (status: OperacaoStatus) => {
    switch (status) {
      case OperacaoCommonStatus.EmAnalise:
        return <Error fontSize="small" className={classes.waitingIcon} />;
      case CompraVendaStatus.Cancelada:
      case OperacaoCommonStatus.Reprovada:
        return <Cancel fontSize="small" className={classes.errorIcon} />;
      case OperacaoCommonStatus.Concluida:
        return <CheckCircle fontSize="small" className={classes.successIcon} />;
      case OfertaPrimariaStatus.Agendado:
        return <CalendarToday fontSize="small" style={{ marginLeft: 10 }} />
      case CompraVendaStatus.AguardandoLiquidacao:
        return <WatchLater fontSize="small" className={classes.warningIcon} />;
      case OfertaPrimariaStatus.AguardandoConfirmacao:
        return <ReportProblemOutlined fontSize="small" style={{ marginLeft: 10 }} />;
      default:
        return <></>;
    }
  };

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith'].includes(value)
  );

  const filterOperatorsData = getGridDateOperators();

  const getGridColumns = useCallback(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 100,
        minWidth: 100,
        maxWidth: 200,
        renderCell: (params: GridCellParams) => {
          const ofertaPrimaria = params.row as OperacaoOfertaPrimariaResponse;
          return (
            <>
              <Tooltip
                title={operacaoStatusDescriptions[ofertaPrimaria.status]}
                aria-label={operacaoStatusDescriptions[ofertaPrimaria.status]}
              >
                {getStatusIcon(ofertaPrimaria.status)}
              </Tooltip>
            </>
          );
        },
      },
      {
        field: 'CRIADO_EM',
        headerName: 'Criado em',
        type: 'date',
        filterOperators: filterOperatorsData,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const ofertaPrimaria = params.row as OperacaoOfertaPrimariaResponse;
          return formatDate(ofertaPrimaria.dataDeCriacao);
        },
        width: 125,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'DATA_LIQUIDACAO',
        headerName: 'Data de Liquidação',
        type: 'date',
        filterOperators: filterOperatorsData,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const ofertaPrimaria = params.row as OperacaoOfertaPrimariaResponse;
          return formatDate(ofertaPrimaria.instrumentoFinanceiro.dataDeLiquidacao);
        },
        width: 125,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'tipo',
        headerName: 'Tipo',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          operacaoTipoDescriptions[(params.row as OperacaoOfertaPrimariaResponse).tipo],
        width: 125,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'codigo',
        headerName: 'Código',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as OperacaoOfertaPrimariaResponse).instrumentoFinanceiro.codigo,
        width: 150,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'RAZAO_SOCIAL_CUSTODIANTE',
        headerName: 'Razão Social Custodiante',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as OperacaoOfertaPrimariaResponse).custodiante.razaoSocial,
        width: 220,
        minWidth: 150,
        maxWidth: 300,
      },
      {
        field: 'RAZAO_SOCIAL_EMISSOR',
        headerName: 'Razão Social Emissor',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as OperacaoOfertaPrimariaResponse).instrumentoFinanceiro.razaoSocialEmissor,
        width: 220,
        minWidth: 150,
        maxWidth: 300,
      },
      colunaDocumentoOfertaPrimaria,
      ...colunasRelatorioMovimentacao,
      {
        field: 'actions',
        headerName: ' ',
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params: GridCellParams) => {
          const operacao = params.row as OperacaoOfertaPrimariaResponse;
          const menuItems = [];

          if (!isUsuarioLeitura) {
            const aprovarFunction = onAprovarFunction(operacao);
            if (aprovarFunction) {
              menuItems.push(
                <MenuItem key="aprovar" onClick={aprovarFunction}>
                  <CheckCircle fontSize="small" style={{ marginRight: '10px' }} />
                  {'Aprovar'}
                </MenuItem>
              );
            }

            const confirmarFunction = onConfirmarFunction(operacao);
            if (confirmarFunction) {
              menuItems.push(
                <MenuItem key="confirmar" onClick={confirmarFunction}>
                  <CheckCircle fontSize="small" style={{ marginRight: '10px' }} />
                  {'Confirmar Liquidação'}
                </MenuItem>
              );
            }

            const reprovarFunction = onReprovarFunction(operacao);
            if (reprovarFunction) {
              menuItems.push(
                <MenuItem key="reprovar" onClick={reprovarFunction}>
                  <Cancel fontSize="small" style={{ marginRight: '10px' }} />
                  {'Reprovar'}
                </MenuItem>
              );
            }
          }

          if (onDetalhesFunction) {
            menuItems.push(
              <MenuItem
                key="detalhes"
                onClick={() => {
                  onDetalhesFunction(operacao);
                }}
              >
                <Description fontSize="small" style={{ marginRight: '10px' }} />
                {'Ver Detalhes'}
              </MenuItem>
            );

            menuItems.push(
              <MenuItem
                key="gestaoComprovantes"
                onClick={() => handleOpenGestaoComprovantesDrawer(operacao)}
              >
                <AttachFile fontSize="small" style={{ marginRight: '10px' }} />
                {'Gestão de Comprovantes'}
              </MenuItem>
            );

            if (operacao.status === OfertaPrimariaStatus.Agendado) {
              menuItems.push(
                <MenuItem
                  key="editarAgendamento"
                  onClick={() => {
                    handleOpenAgendamentoDrawer(operacao);
                  }}
                >
                  <Edit fontSize="small" style={{ marginRight: '10px' }} />
                  {'Editar Agendamento'}
                </MenuItem>
              );
            }
          }

          return <MoreOptionsMenu>{menuItems}</MoreOptionsMenu>;
        },
        width: 75,
        minWidth: 75,
        maxWidth: 200,
      },
    ],
    []
  );
  return (
    <div>
      {showStatusToast && statusToastByAction(drawerAction)}
      <MdSpace />
      <LegendaStatus status={onStatusClick} />
      <MdSpace />
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={{
          ...componentsPropsValues,
          toolbar: {
            carregarTodasAsMovimentacoes,
            movimentacoesDaPaginaAtual: operacoes,
            nomeArquivo: 'movimentacoes-oferta-primaria',
          },
        }}
        rows={operacoes}
        columns={getGridColumns()}
        components={{
          Toolbar: ToolbarExportacaoMovimentacoes,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize), setPage(0);
        }}
        pageSize={pageSize}
        loading={showLoader}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
      />
      <MdSpace />
      <GenericDrawer
        opened={openDrawer}
        disableButtons={false}
        onClose={handleCloseDrawer}
        title={`${drawerAction === DrawerAction.APROVAR ? 'Aprovar' : 'Reprovar'} Movimentação`}
        textContents={[
          {
            text: `Tem certeza que deseja ${drawerAction === DrawerAction.APROVAR ? 'aprovar' : 'reprovar'
              } a movimentação referente ao instrumento financeiro ${operacao?.instrumentoFinanceiro.codigo ?? ''}?`,
          },
        ]}
        buttons={[
          {
            buttonText: drawerAction === DrawerAction.APROVAR ? 'Aprovar' : 'Reprovar',
            buttonAction: drawerAction === DrawerAction.APROVAR ? () => handleAprovar() : () => handleReprovar(),
            buttonColor: drawerAction === DrawerAction.APROVAR ? 'primary' : 'error',
          },
          {
            buttonText: 'Cancelar',
            buttonAction: () => handleCloseDrawer(),
          },
        ]}
      />

      <Drawer
        anchor="right"
        open={openAgendamentoDrawer}
        onClose={handleCloseAgendamentoDrawer}
        PaperProps={{ style: { padding: '40px', width: 520 } }}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h3">Gerenciar Agendamento</Typography>
            <SmSpace />
            <Divider variant="fullWidth" />
            <SmSpace />
          </Grid>

          {agendamentoAtual && (
            <Grid item xs={12} style={{ marginBottom: '20px' }}>
              <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '10px' }}>Informações do Agendamento Atual</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold', color: '#666' }}>Data Atual:</Typography>
                  <Typography variant="body2">{moment(agendamentoAtual.dataAgendamento).format('DD/MM/YYYY')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold', color: '#666' }}>Última Atualização:</Typography>
                  <Typography variant="body2">{moment(agendamentoAtual.dataAtualizacao).format('DD/MM/YYYY HH:mm')}</Typography>
                </Grid>
              </Grid>
              <SmSpace />
              <Divider variant="fullWidth" />
              <SmSpace />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="body2">
              Gerenciamento de agendamento para a oferta primária referente ao instrumento financeiro <b>{operacao?.instrumentoFinanceiro.codigo ?? ''}</b>.
            </Typography>
            <MdSpace />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2">Selecione uma nova data de agendamento:</Typography>
            <SmSpace />
            <DatePickerBusinessDay
              value={dataAgendamento}
              onChange={setDataAgendamento}
              label="Data de Agendamento"
              keyValue="dataAgendamento"
              required
              validateBusinessDay={true}
              minDate={moment().add(1, 'day')}
            />
            <LgSpace />
          </Grid>

          <Grid item xs={12}>
            <Button
              onClick={handleEditarAgendamento}
              variant="contained"
              color="primary"
              fullWidth
              disabled={!dataAgendamento}
            >
              Editar Data Agendamento
            </Button>
            <SmSpace />
            <Button
              onClick={handleCancelarAgendamento}
              variant="contained"
              color="error"
              fullWidth
            >
              Cancelar Agendamento
            </Button>
            <SmSpace />
            <Button
              onClick={handleCloseAgendamentoDrawer}
              variant="outlined"
              fullWidth
            >
              Voltar
            </Button>
          </Grid>
        </Grid>
      </Drawer>

      <Drawer
        anchor="right"
        open={openGestaoComprovantesDrawer}
        onClose={handleCloseGestaoComprovantesDrawer}
        PaperProps={{ style: { padding: '40px', width: 520 } }}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h3">Gestão de Comprovantes</Typography>
            <SmSpace />
            <Typography variant="body2" color="textSecondary">
              {`Instrumento: ${operacao?.instrumentoFinanceiro.codigo ?? ''}`}
            </Typography>
            <SmSpace />
            <Divider variant="fullWidth" />
            <SmSpace />
          </Grid>

          <Grid item xs={12}>
            <UploadDocs
              multipleFiles
              onChange={(atts) => setGestaoComprovantesAttachments((prev) => [...prev, ...atts])}
              onDelete={(id) => setGestaoComprovantesAttachments((prev) => prev.filter((att) => att.id !== id))}
            />
          </Grid>

          <Grid item xs={12}>
            <MdSpace />
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
              Comprovantes Existentes
            </Typography>
            <SmSpace />
            {loadingComprovantes && (
              <Typography variant="body2" color="textSecondary">Carregando...</Typography>
            )}
            {!loadingComprovantes && comprovantesExistentes.length === 0 && (
              <Typography variant="body2" color="textSecondary">Nenhum comprovante anexado.</Typography>
            )}
            {!loadingComprovantes && comprovantesExistentes.map((comp) => (
              <React.Fragment key={comp.id}>
                <Grid container alignItems="center" spacing={1} style={{ paddingTop: 8, paddingBottom: 8 }}>
                  <Grid item>
                    <Description fontSize="small" />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2">
                      {`${comp.fileName}${comp.extension} - ${(comp.size / (1024 * 1024)).toFixed(2)}MB`}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<GetApp fontSize="small" />}
                      onClick={() => {
                        if (comp.url) {
                          window.open(comp.url, '_blank');
                        }
                      }}
                    >
                      Download
                    </Button>
                  </Grid>
                </Grid>
                <Divider variant="fullWidth" />
              </React.Fragment>
            ))}
          </Grid>

          <Grid item xs={12}>
            <LgSpace />
            <Button
              onClick={handleSalvarComprovantes}
              variant="contained"
              color="primary"
              fullWidth
              disabled={gestaoComprovantesAttachments.length === 0 || isSavingComprovantes}
            >
              {isSavingComprovantes ? 'Enviando...' : 'Enviar Comprovante'}
            </Button>
            <SmSpace />
            <Button
              onClick={handleCloseGestaoComprovantesDrawer}
              variant="outlined"
              fullWidth
            >
              Fechar
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </div>
  );
};

export default OfertasPrimarias;
